import React, { useCallback, useRef } from 'react'
import { MetaData } from 'components/MetaData'
import { useProductListPage, useCategoryCmsBlock } from 'lib/modules/catalog'
import { EmptyContainer, Spinner, FadeIn, FlexWrapper } from 'lib/ui'
import { useIsMobile, useIsXs } from 'hooks/responsive'
import { ProductList } from 'components/ProductList'
import { Breadcrumbs } from 'components/Breadcrumbs'
import { LayeredNav } from 'components/LayeredNav'
import { LayeredControls } from 'components/LayeredControls'
import {
  Wrapper,
  Container,
  Sidebar,
  ContentWrapper,
  DescriptionWrapper,
  BannerWrapper
} from './styled'

interface Props {
  title: string
  breadcrumbTitle?: string
  categoryId?: number
  loadingState?: boolean
  description?: string
}

export const ProductListLayout = ({
  categoryId,
  title,
  breadcrumbTitle,
  loadingState,
  description
}: Props) => {
  const categoryTopRef = useRef<HTMLDivElement>(null)
  const isMobile = useIsMobile()
  const isTablet = useIsXs()

  const scrollTop = () => {
    window.scroll({ top: (categoryTopRef.current?.offsetTop ?? 0 - 20), left: 0, behavior: 'smooth' })
  }

  const { isError, isFetching, isLoading, products, setFilters } = useProductListPage({
    categoryId,
    enableGTM: true,
    withProducts: true
  })

  const { cmsBlock } = useCategoryCmsBlock(categoryId ?? 0)

  const handleClearFilters = useCallback(() => {
    scrollTop()
    setFilters({})
  }, [setFilters])

  return (
    <Wrapper>
      <FadeIn>
        <MetaData
          type={categoryId ? 'PLP' : undefined}
          title={categoryId ? undefined : title}
        />
        <Container>
          {!isMobile && (
            <Breadcrumbs title={breadcrumbTitle} />
          )}

          {cmsBlock?.content && (
            <BannerWrapper
              dangerouslySetInnerHTML={{
                __html: cmsBlock?.content
              }}
            />
          )}

          <div
            ref={categoryTopRef}
          />

          {(isLoading || loadingState) ? (
            <EmptyContainer>
              <Spinner />
            </EmptyContainer>
          ) : (
            <div className='content-wrapper'>
              <FlexWrapper>
                {!isTablet && (
                  <Sidebar>
                    <LayeredNav
                      categoryId={categoryId}
                      onFilterChange={scrollTop}
                      onClearFiltersPress={handleClearFilters}
                    />
                  </Sidebar>
                )}
                <ContentWrapper>
                  <div className='toolbar'>
                    <LayeredControls
                      categoryId={categoryId}
                      showPagination={false}
                      title={title}
                    />
                  </div>
                  <ProductList
                    listPage={categoryId ? 'Category Page' : 'Search Page'}
                    isError={isError}
                    isFetching={isFetching}
                    isLoading={isLoading}
                    products={products}
                    clearFiltersHandler={() => setFilters({})}
                  />

                  <div className='toolbar__bottom'>
                    <LayeredControls
                      categoryId={categoryId}
                      showSorter={false}
                      showTotalCount={false}
                      onChange={scrollTop}
                      showLayeredNavAction={false}
                    />
                  </div>

                  <DescriptionWrapper>
                    {description}
                  </DescriptionWrapper>
                </ContentWrapper>
              </FlexWrapper>
            </div>
          )}
        </Container>
      </FadeIn>
    </Wrapper>
  )
}
